import React, { isValidElement, Children, Fragment } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/utils/cn';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  disabled?: boolean;
  contentClassName?: string;
}

export const Tooltip = React.forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      children,
      content,
      side = 'top',
      align = 'center',
      delayDuration = 400,
      disabled = false,
      contentClassName,
      ...props
    },
    ref
  ) => {
    if (disabled) return <>{children}</>;

    if (!children) return null;

    const trigger = (() => {
      const count = Children.count(children);
      if (count > 1) {
        return <span className={styles.tooltipTriggerWrapper}>{children}</span>;
      }
      const onlyChild = Children.only(children);
      if (!isValidElement(onlyChild) || onlyChild.type === Fragment) {
        return <span className={styles.tooltipTriggerWrapper}>{children}</span>;
      }
      return onlyChild;
    })();

    return (
      <TooltipPrimitive.Provider>
        <TooltipPrimitive.Root delayDuration={delayDuration}>
          <TooltipPrimitive.Trigger asChild ref={ref} {...props}>
            {trigger}
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              className={cn(styles.tooltipContent, contentClassName)}
              side={side}
              align={align}
              sideOffset={8}
            >
              {content}
              <TooltipPrimitive.Arrow className={styles.tooltipArrow} />
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }
);

Tooltip.displayName = 'Tooltip';
export const TooltipProvider = TooltipPrimitive.Provider;
